import { DescriptionList } from "@/lib/Components/DescriptionList/DescriptionListCard";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { MailIcon, PhoneIcon } from "lucide-react";
import { Badge } from "@/components/catalyst/badge";
import { useUserRecord } from "@/app/Users/Hooks/useUserRecord";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { Switch, SwitchField } from "@/components/catalyst/switch";
import { Description, Label } from "@/components/catalyst/fieldset";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateUserNotificationSettingsMutation } from "@/app/Users/GraphQL/userMutations";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";

export function UserDetailsScreen() {
  const record = useUserRecord();
  const { mutate } = useGqlMutation(updateUserNotificationSettingsMutation);

  return (
    <div className="space-y-6 py-6">
      <RecordScreenCard title="User details">
        <DescriptionList
          items={[
            {
              key: "Name",
              value: record.name ?? "--",
            },
            {
              key: "Email",
              value: record.primary_email ?? "--",
            },
            {
              key: "Phone",
              value: record.primary_phone ?? "--",
            },
            {
              key: "Date of birth",
              value: record.date_of_birth ?? "--",
            },
            {
              key: "Created",
              value: record.created_at ? (
                <TimeSinceNow time={record.created_at} />
              ) : (
                "--"
              ),
            },
            {
              key: "Updated",
              value: record.updated_at ? (
                <TimeSinceNow time={record.updated_at} />
              ) : (
                "--"
              ),
            },
          ]}
        />
      </RecordScreenCard>

      <RecordScreenCard title="Email notifications">
        <div className="space-y-4 px-4 py-4">
          {record.email_undeliverable_at ? (
            <WarningBanner>
              Email undeliverable since{" "}
              <TimeSinceNow time={record.email_undeliverable_at} />
              {record.email_undeliverable_reason
                ? `: ${record.email_undeliverable_reason}`
                : null}
              . Notifications were turned off. Turning them back on clears this.
            </WarningBanner>
          ) : null}
          <SwitchField>
            <Label>Marketing and reminder emails</Label>
            <Description>
              Off means they unsubscribed. Waitlist matches, pro driver job
              offers, new message notifications, reminders and review requests
              are withheld; booking and account emails are always sent.
            </Description>
            <Switch
              className="row-span-2 row-start-1"
              checked={
                record.notificationSettings?.gets_email_notifications ?? true
              }
              onChange={(checked) =>
                mutate({
                  user_id: record.id,
                  gets_email_notifications: checked,
                })
              }
            />
          </SwitchField>
        </div>
      </RecordScreenCard>

      {record.contact?.billing_email ? (
        <RecordScreenCard title="Billing email">
          <div className="flex items-center gap-3 px-4 py-4 text-sm">
            <MailIcon className="h-5 w-5 shrink-0 text-gray-400" />
            <span className="font-medium">{record.contact.billing_email}</span>
            {record.contact.billing_email === record.primary_email ? (
              <Badge color="indigo">Primary</Badge>
            ) : null}
          </div>
        </RecordScreenCard>
      ) : null}

      {record.contact?.billing_phone ? (
        <RecordScreenCard title="Billing phone">
          <div className="flex items-center gap-3 px-4 py-4 text-sm">
            <PhoneIcon className="h-5 w-5 shrink-0 text-gray-400" />
            <span className="font-medium">{record.contact.billing_phone}</span>
            {record.contact.billing_phone === record.primary_phone ? (
              <Badge color="indigo">Primary</Badge>
            ) : null}
          </div>
        </RecordScreenCard>
      ) : null}
    </div>
  );
}
